import { create } from "zustand";
import { persist } from "zustand/middleware";
import { ChatCardType } from "../feature/utils/LeftPanel.types";
import { useContactStore } from "./useContactStore";
import { fetchAllGroups } from "../services/groupService";

import { useEffect } from "react";
import { useAuthStore } from "./useAuthStore";
import { useAuth } from "../hooks/authBackoffice";
import {
  saveMessageToIndexedDB,
  updateMessageDurationInDB,
  loadMessagesFromIndexedDB,
} from "../feature/utils/indexedDB";
import { ReactionData } from "../feature/pages/homepage/ChatContainer";
import { getLastMessage } from "../utils/getLastMessage";

export type DurationOption =
  | "1 minuto"
  | "1 hora"
  | "6 horas"
  | "24 horas"
  | "3 dias"
  | "7 dias"
  | "30 dias"
  | "60 dias"
  | "90 dias"
  | "Desativada";

export interface Message {
  id: string;
  content: string | Blob | File;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  chatId?: string;
  timestamp: string;
  expiresAt?: number;
  status: "sent" | "delivered" | "seen" | "failed";
  _retryEvent?: string;
  _retryPayload?: any;
  type?:
    | "text"
    | "audio/webm"
    | "image"
    | "video"
    | "pdf"
    | "document"
    | "deleted"
    | "broadcast"
    | "call"
    | "system_message";
  senderId: string;
  isTemporary?: boolean;
  isDeletedForAll?: boolean;
  isDeletedForMe?: boolean;
  viewOnce: boolean;
  isOwn: boolean;
  viewedAt: Date | null;
  encryptedKey?: string;
  iv?: string;
  receiverId?: string;
  groupId?: string;
  subtitle?: string;
  isFav?: boolean;
  isEdited: boolean | null;
  reaction: ReactionData[];
  callInfo?: {
    type: "voice" | "video";
    status: "answered" | "unanswered" | "rejected" | "canceled" | "busy";
    duration?: number;
  };
  replyTo?: {
    messageId: string;
    content: string;
    type: string;
    senderId: string;
    senderName?: string;
  };
}

export interface ChatState {
  chats: ChatCardType[];
  chatMessages: Record<string, Message[]>; // { [chatId]: Message[] }
  lastChat: string;
  chatId: string;
  selectedChat: ChatCardType | null;
  messageDuration: number | null;
  messageToScrollTo: string | null; // ID da mensagem para rolar
  replyToMessage: Message | null; // Mensagem que está sendo respondida

  // Funções de manipulação de chats

  setChat: (chats: ChatCardType[]) => void;
  addChat: (chat: ChatCardType) => void;
  addOrUpdateChat: (newChat: ChatCardType) => void;
  removeChatByName: (name: string) => void;
  setLastChat: (chatId: string) => void;
  setChatId: (chatId: string) => void;
  setSelectedChat: (chat: ChatCardType | null) => void;
  setMessageToScrollTo: (messageId: string | null) => void; // Ação para definir a mensagem
  setReplyToMessage: (message: Message | null) => void; // Ação para definir a mensagem de resposta
  updateArchivedChat: (chatId: string, isArchived: boolean) => void;
  updateFixedChat: (chatId: string, isFixed: boolean) => void;

  updateFavChat: (chatId: string, isFav: boolean) => void;
  updateBlockedChat: (chatId: string, blocked: boolean) => void;
  updateDurationMessage: (chatId: string, messageDuration: number | null) => void;
  // Funções de mensagens
  swipedCardId: string | null;
  setSwipedCardId: (id: string | null) => void;
  addMessage: (chatId: string, message: Message) => void;
  getMessages: (chatId: string) => Message[];

  // Funções auxiliares
  fetchChats: (userId: string, userName: string, token: string) => void;

  setMessageDuration: (chatId: string, duration: DurationOption) => void;

  cleanupExpiredMessages: () => void;

  updateChatWithNewMessage: (
    targetChatId: string,
    newMessage: Message,
    currentUserId: string,
    isNewChat?: boolean // Flag para indicar se é um chat completamente novo
  ) => void;
  clearUnreadMessages: (chatId: string) => void;
  refreshChatPreview: (chatId: string, currentUserId: string) => Promise<void>;

  toggleFavoriteMessage: (chatId: string, messageId: string) => Promise<Message | undefined>;

  setChatMessages: (messages: Record<string, Message[]>) => void;

  removeChatById: (id: string) => void;
  updateChatMemberCount: (chatId: string, change: number) => void;
  updateGroupDetails: (groupId: string, newDetails: any) => void;
}

export const durationToMs = (duration: DurationOption): number | null => {
  switch (duration) {
    case "1 minuto":
      return 60 * 1000;
    case "1 hora":
      return 60 * 60 * 1000;
    case "6 horas":
      return 6 * 60 * 60 * 1000;
    case "24 horas":
      return 24 * 60 * 60 * 1000;
    case "3 dias":
      return 3 * 24 * 60 * 60 * 1000;
    case "7 dias":
      return 7 * 24 * 60 * 60 * 1000;
    case "30 dias":
      return 30 * 24 * 60 * 60 * 1000;
    case "60 dias":
      return 60 * 24 * 60 * 60 * 1000;
    case "90 dias":
      return 90 * 24 * 60 * 60 * 1000;
    case "Desativada":
      return null;
    default:
      return null;
  }
};

export const DurationMsToText = (duration: number | null | undefined): DurationOption => {
  switch (duration) {
    case 60 * 1000:
      return "1 minuto";
    case 60 * 60 * 1000:
      return "1 hora";
    case 6 * 60 * 60 * 1000:
      return "6 horas";
    case 24 * 60 * 60 * 1000:
      return "24 horas";
    case 3 * 24 * 60 * 60 * 1000:
      return "3 dias";
    case 7 * 24 * 60 * 60 * 1000:
      return "7 dias";
    case 30 * 24 * 60 * 60 * 1000:
      return "30 dias";
    case 60 * 24 * 60 * 60 * 1000:
      return "60 dias";
    case 90 * 24 * 60 * 60 * 1000:
      return "90 dias";

    default:
      return "Desativada";
  }
};

function formatLastMessageContentForPreview(message: Message | undefined): string {
  if (!message) return "";
  if (message.type === "deleted" || message.isDeletedForAll) return "Mensagem apagada";
  if (message.isDeletedForMe) return "Você apagou esta mensagem"; // Se quiser diferenciar

  return message.type === "image"
    ? "📷 Imagem" // Adicionando ícones para melhor UX
    : message.type === "pdf"
    ? "📄 Arquivo"
    : message.type === "document"
    ? "📄 Documento"
    : message.type === "video"
    ? "🎥 Vídeo"
    : message.type === "audio/webm"
    ? "🎤 Áudio"
    : message.type === "call"
    ? "📲​ Ligação"
    : typeof message.content === "string" && !message.viewOnce
    ? message.content.length > 30
      ? message.content.substring(0, 27) + "..."
      : message.content // Truncar se muito longo
    : message.viewOnce
    ? "👁️ Visualização única"
    : "Nova mensagem"; // Fallback
}

function formatLastMessageTimeForPreview(timestamp: string | undefined): string {
  if (!timestamp) return "";
  const messageDate = new Date(timestamp);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (messageDate >= today) {
    return messageDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (messageDate >= yesterday) {
    return "Ontem";
  }
  const yearFormat = messageDate.getFullYear() === now.getFullYear() ? undefined : "numeric";
  return messageDate.toLocaleDateString([], { day: "2-digit", month: "2-digit", year: yearFormat });
}

export const useChatStore = create<ChatState>()(persist((set, get) => ({
  chats: [],
  chatMessages: {},
  lastChat: "",
  chatId: "",
  selectedChat: null,
  messageDuration: null,
  messageToScrollTo: null,
  replyToMessage: null,
  swipedCardId: null, // Estado inicial: nenhum card está aberto

  setSwipedCardId: (id) => set({ swipedCardId: id }),

  // Manipulação de chats
  setChat: (chats) => {
    set((state) => {
      // Preservar chats de broadcast existentes
      const broadcastChats = state.chats.filter(
        (chat) => chat.type === "BROADCAST" || chat.id?.startsWith("broadcast_")
      );

      // Combinar novos chats com chats de broadcast preservados
      const combinedChats = [...chats, ...broadcastChats];

      // Remover duplicatas (caso algum chat de broadcast já esteja nos novos chats)
      const uniqueChats = combinedChats.filter(
        (chat, index, self) => index === self.findIndex((c) => c.id === chat.id)
      );

      return { chats: uniqueChats };
    });
  },

  addChat: (chat) => {
    set((state) => {
      const chatExists = state.chats.some((c) => c.id === chat.id);
      if (chatExists) {
        return state;
      }

      // Aplica a configuração de duração padrão para novos chats
      const defaultDuration = state.messageDuration;
      const newChatWithDuration = {
        ...chat,
        messageDuration: defaultDuration !== null ? defaultDuration : undefined,
        isTemporary: defaultDuration !== null,
      };

      return { chats: [newChatWithDuration, ...state.chats] };
    });
  },

  updateGroupDetails: (groupId, newDetails) => {
    set((state) => ({
      chats: state.chats.map((chat) => {
        // Encontra o chat que corresponde ao ID do grupo
        if (chat.id === groupId || chat.groupId === groupId) {
          // Retorna um novo objeto de chat com os detalhes atualizados
          return { ...chat, ...newDetails };
        }
        // Para todos os outros chats, retorna eles sem alteração
        return chat;
      }),
    }));
  },

  addOrUpdateChat: (newChat) => {
    set((state) => {
      // Cria uma cópia da lista de chats atual para evitar mutação direta
      const updatedChats = [...state.chats];

      // Procura se o chat já existe na lista
      const existingChatIndex = updatedChats.findIndex((chat) => chat.id === newChat.id);

      if (existingChatIndex > -1) {
        // Se o chat já existe, ele é atualizado.
        // Isso é útil para eventos de 'group_updated', por exemplo.
        updatedChats[existingChatIndex] = {
          ...updatedChats[existingChatIndex], // Mantém dados antigos
          ...newChat, // Sobrescreve com os novos dados
        };
      } else {
        // Se o chat não existe, ele é adicionado no topo da lista
        updatedChats.unshift(newChat);
      }

      // Reordena para garantir que os fixados estejam sempre no topo
      updatedChats.sort((a, b) => {
        if (a.isFixed && !b.isFixed) return -1;
        if (!a.isFixed && b.isFixed) return 1;
        const timeA = a.lastSeen ? new Date(a.lastSeen).getTime() : 0;
        const timeB = b.lastSeen ? new Date(b.lastSeen).getTime() : 0;
        return timeB - timeA;
      });

      return { chats: updatedChats };
    });
  },

  removeChatByName: (name) =>
    set((state) => ({
      chats: state.chats.filter((chat) => chat.name !== name),
    })),

  setLastChat: (chatId) => set({ lastChat: chatId }),
  setChatId: (chatId) => set({ chatId }),
  setSelectedChat: (chat) => {
    const state = get();
    const chatToSet = state.chats.find((c) => c.id === chat?.id) || chat;

    if (!chatToSet) {
      set({ selectedChat: null });
      return;
    }

    const hasSpecificDuration = chatToSet.messageDuration !== null && chatToSet.messageDuration !== undefined;

    if (!hasSpecificDuration && state.messageDuration !== null) {
      const defaultDuration = state.messageDuration;

      const updatedChat = {
        ...chatToSet,
        messageDuration: defaultDuration,
        isTemporary: true,
      };

      const updatedChats = state.chats.map((c) => (c.id === updatedChat.id ? updatedChat : c));

      set({
        chats: updatedChats,
        selectedChat: updatedChat,
        messageToScrollTo: null,
      });
    } else {
      set({ selectedChat: chatToSet, messageToScrollTo: null });
    }
  },
  setMessageToScrollTo: (messageId) => set({ messageToScrollTo: messageId }),
  setReplyToMessage: (message) => set({ replyToMessage: message }),

  removeChatById: (id) =>
    set((state) => ({
      chats: state.chats.filter((chat) => chat.id !== id),
      // Opcional: também limpa as mensagens do chat removido
      chatMessages: (({ [id]: _, ...rest }) => rest)(state.chatMessages),
      // Se o chat removido era o selecionado, limpa a seleção
      selectedChat: state.selectedChat?.id === id ? null : state.selectedChat,
    })),

  // Manipulação de mensagens
  addMessage: (chatId, message) => {
    const chat = get().chats.find((c) => c.id === chatId);
    const durationMs = chat?.messageDuration || null;

    const expiresAt = durationMs ? Date.now() + durationMs : undefined;
    const isTemporary = !!durationMs;

    set((state) => {
      const existingMessages = state.chatMessages[chatId] || [];
      const newMessage = { ...message, expiresAt, isTemporary };
      const updatedMessages = [...existingMessages, newMessage].sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      return {
        chatMessages: {
          ...state.chatMessages,
          [chatId]: updatedMessages,
        },
      };
    });
  },

  updateChatMemberCount: (chatId, change) => {
    set((state) => {
      // Encontra o chat na lista
      const chatIndex = state.chats.findIndex((c) => c.id === chatId);
      if (chatIndex === -1) {
        return state; // Retorna o estado sem mudanças se o chat não for encontrado
      }

      const updatedChats = [...state.chats];
      const chatToUpdate = { ...updatedChats[chatIndex] };

      // Atualiza a contagem de membros
      chatToUpdate.memberCount = (chatToUpdate.memberCount || 0) + change;

      // Garante que a contagem não seja negativa
      if (chatToUpdate.memberCount < 0) {
        chatToUpdate.memberCount = 0;
      }

      updatedChats[chatIndex] = chatToUpdate;

      // Também atualiza o selectedChat se for o mesmo
      const updatedSelected = state.selectedChat?.id === chatId ? chatToUpdate : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },

  getMessages: (chatId) => get().chatMessages[chatId] || [],

  setMessageDuration: (chatId, duration) => {
    console.log(`Setting duration for ${chatId}:`, duration);
    const durationMs = durationToMs(duration);
    console.log("Converted to ms:", durationMs);

    set((state) => {
      // Atualiza a lista de chats
      const updatedChats = state.chats.map((chat) =>
        chat.id === chatId ? { ...chat, messageDuration: durationMs } : chat
      );

      // Atualiza o selectedChat se for o mesmo chat
      const updatedSelected =
        state.selectedChat?.id === chatId ? { ...state.selectedChat, messageDuration: durationMs } : state.selectedChat;

      // Limpa mensagens expiradas
      const now = Date.now();
      const updatedChatMessages = { ...state.chatMessages };
      if (updatedChatMessages[chatId]) {
        updatedChatMessages[chatId] = updatedChatMessages[chatId].filter(
          (msg) => !msg.isTemporary || !msg.expiresAt || msg.expiresAt > now
        );
      }

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
        chatMessages: updatedChatMessages,
        messageDuration: durationMs,
      };
    });
  },

  // Limpeza de mensagens expiradas
  cleanupExpiredMessages: () => {
    const now = Date.now();
    set((state) => {
      const newChatMessages = { ...state.chatMessages };

      Object.keys(newChatMessages).forEach((chatId) => {
        newChatMessages[chatId] = newChatMessages[chatId].filter((msg) => {
          // Mantém mensagens que não são temporárias ou ainda não expiraram
          return !msg.isTemporary || !msg.expiresAt || msg.expiresAt > now;
        });
      });

      return { chatMessages: newChatMessages };
    });
  },

  updateArchivedChat: (chatId: string, isArchived: boolean) => {
    set((state) => {
      const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, isArchived } : chat));

      const updatedSelected =
        state.selectedChat?.id === chatId ? { ...state.selectedChat, isArchived } : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },

  updateFixedChat: (chatId: string, isFixed: boolean) => {
    set((state) => {
      const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, isFixed } : chat));

      const updatedSelected =
        state.selectedChat?.id === chatId ? { ...state.selectedChat, isFixed } : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },

  updateFavChat: (chatId: string, isFav: boolean) => {
    set((state) => {
      const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, isFav } : chat));

      const updatedSelected = state.selectedChat?.id === chatId ? { ...state.selectedChat, isFav } : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },
  updateBlockedChat: (chatId: string, blocked: boolean) => {
    set((state) => {
      const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, blocked } : chat));

      const updatedSelected = state.selectedChat?.id === chatId ? { ...state.selectedChat, blocked } : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },
  updateDurationMessage: (chatId: string, messageDuration: number | null) => {
    set((state) => {
      const updatedChats = state.chats.map((chat) => (chat.id === chatId ? { ...chat, messageDuration } : chat));

      const updatedSelected =
        state.selectedChat?.id === chatId ? { ...state.selectedChat, messageDuration } : state.selectedChat;

      return {
        chats: updatedChats,
        selectedChat: updatedSelected,
      };
    });
  },

  fetchChats: async (userId: string, userName: string, token: string) => {
    // Salva os chats existentes para restaurar se tudo falhar
    const existingChats = get().chats;
    let contacts: any[] = [];
    let groups: any[] = [];
    let contactsLoaded = false;
    let groupsLoaded = false;

    try {
      // Tenta buscar contatos
      const contactStore = useContactStore.getState();
      await contactStore.listContactsByUserId(userId);
      const fetchedContacts = contactStore.getContacts();
      if (Array.isArray(fetchedContacts) && fetchedContacts.length > 0) {
        contacts = fetchedContacts;
        contactsLoaded = true;
      } else if (Array.isArray(fetchedContacts) && fetchedContacts.length === 0) {
        // Se retornou 0, pode ser que a API falhou e preservou a lista anterior.
        // Usamos os contatos da store diretamente para evitar apagar a lista.
        const existingContacts = useContactStore.getState().contacts;
        if (existingContacts.length > 0) {
          contacts = existingContacts;
        }
        contactsLoaded = contacts.length > 0;
      } else {
        console.log("A busca de contatos não retornou um array. Continuando sem eles.");
      }
    } catch (error) {
      console.error("Erro CRÍTICO ao buscar contatos:", error);
    }

    try {
      // Tenta buscar grupos
      const groupsResponse = await fetchAllGroups(userName, token);
      console.log("groupsResponse", groupsResponse);

      if (Array.isArray(groupsResponse)) {
        groups = groupsResponse;
        groupsLoaded = true;
      } else {
        console.log("A busca de grupos não retornou um array. Continuando sem eles.");
      }
    } catch (error) {
      console.error("Erro CRÍTICO ao buscar grupos:", error);
    }

    try {
      const messages = await loadMessagesFromIndexedDB(userId || "");
      console.log("[ChatStore] Mensagens carregadas do IndexedDB:", Object.keys(messages).length);

      const formattedContacts = contacts.map((contact: any) => {
        const lastMessage = getLastMessage(contact.contact.id, messages, userId);
        return {
          id: contact.contact.id,
          name: contact.contact?.name || "Nome não disponível",
          userName: contact.contact?.userName || "Nome não disponível",
          lastSeen: contact.contact.lastSeen || "",
          unreadMessagesCount: lastMessage.unReadMessages,
          lastText: lastMessage.content || "",
          lastMessageTime: lastMessage?.timestamp ?? "1970-01-01T00:00:00.000Z",
          status: contact.contact.status || "Desconhecido",
          type: contact.contact.type || "USER",
          avatar: contact.contact.avatarLink || undefined, // Inclui avatar do usuário
          messageDuration: contact.messageDuration || null,
          isArchived: contact.isArchived,
          isFav: contact.isFav,
          blocked: contact.isBlocked ?? false,
          isFixed: contact.isFixed,
          isNotRead: contact.isNotRead,
          created_at: contact.createdAt,
          updated_at: contact.updatedAt,
        };
      });

      const formattedGroups = groups.map((group: any) => {
        const lastMessage = getLastMessage(group.id, messages, userId);
        return {
          id: group.id,
          name: group.name,
          description: group.description || "Sem descrição",
          memberCount: group.memberCount,
          unreadMessagesCount: lastMessage.unReadMessages,
          lastMessageTime: lastMessage?.timestamp ?? "1970-01-01T00:00:00.000Z",
          lastText: lastMessage.content || "",
          owner: group.ownerUserName,
          ownerId: group.ownerId,
          role: group.role,
          isGroup: true,
          type: "GROUP",
          groupAvatar: group.avatar || undefined, // Inclui avatar do grupo
          messageDuration: group.messageDuration || null,
          isArchived: group.isArchived,
          isFav: group.isFav,
          isFixed: group.isFixed,
          isNotRead: group.isNotRead,
          created_at: group.createdAt,
          updated_at: group.updatedAt,
        };
      });

      const allItems = [...formattedContacts, ...formattedGroups];

      console.log("allItems", allItems);

      // Se não carregou nada novo E já existia dados, mantém os dados existentes
      if (allItems.length === 0 && existingChats.length > 0) {
        console.warn("[ChatStore] fetchChats retornou 0 itens mas existiam chats. Mantendo dados existentes.");
        set({ chatMessages: messages });
        return;
      }

      const uniqueChats = Array.from(new Map(allItems.map((item) => [item.id, item])).values());

      uniqueChats.sort((a, b) => {
        const timeA = a.lastMessageTime ? new Date(a.lastMessageTime).getTime() : 0;
        const timeB = b.lastMessageTime ? new Date(b.lastMessageTime).getTime() : 0;
        return timeB - timeA;
      });

      // Preservar chats de broadcast existentes
      const currentState = get();
      const broadcastChats = currentState.chats.filter(
        (chat) => chat.type === "BROADCAST" || chat.id?.startsWith("broadcast_")
      );

      // Combinar novos chats com chats de broadcast preservados
      const combinedChats = [...uniqueChats, ...broadcastChats];

      // Remover duplicatas (caso algum chat de broadcast já esteja nos novos chats)
      const finalChats = combinedChats.filter((chat, index, self) => index === self.findIndex((c) => c.id === chat.id));

      set({ chats: finalChats, chatMessages: messages });
    } catch (processingError) {
      console.error("Erro durante o processamento e formatação dos chats:", processingError);
    }
  },

  updateChatWithNewMessage: (targetChatId: string, newMessage: Message, currentUserId: string, isNewChat = false) => {
    set((state) => {
      if (isNewChat) {
        console.log(
          `[ChatStore] updateChatWithNewMessage: Recebido 'isNewChat' para ${targetChatId}. ` +
            `Recomendado disparar fetchChats para obter detalhes completos do chat.`
        );
        // Disparar fetchChats de dentro de uma ação da store pode ser complicado devido a dependências (userName, token).
        // É melhor o componente que chama esta ação (ou o listener de socket) decidir chamar fetchChats.
        return state;
      }

      const chatIndex = state.chats.findIndex((c) => c.id === targetChatId);
      if (chatIndex === -1) {
        console.log(
          `[ChatStore] updateChatWithNewMessage: Chat com ID ${targetChatId} não encontrado na lista. ` +
            `Pode ser necessário chamar fetchChats se este for um chat novo para o usuário.`
        );
        return state; // Chat não existe na lista, não faz nada. O listener do socket deve lidar com isso.
      }

      const chats = [...state.chats];
      const chatToUpdate = { ...chats[chatIndex] };

      chatToUpdate.lastText = formatLastMessageContentForPreview(newMessage);
      chatToUpdate.lastSeen = newMessage.timestamp;

      // Incrementa contador de não lidas apenas se:
      // 1. A mensagem não é do usuário atual
      // 2. O chat da mensagem não é o chat atualmente selecionado/aberto
      if (newMessage.senderId !== currentUserId && state.selectedChat?.id !== targetChatId) {
        chatToUpdate.unreadMessagesCount = (chatToUpdate.unreadMessagesCount || 0) + 1;
      }

      // Remove o chat da posição antiga e o coloca no topo
      chats.splice(chatIndex, 1);
      chats.unshift(chatToUpdate);

      return { chats };
    });
  },

  refreshChatPreview: async (chatIdToRefresh: string, currentUserId: string) => {
    console.log(`[ChatStore] Refreshing preview for chat: ${chatIdToRefresh}`);
    const allUserMessages = await loadMessagesFromIndexedDB(currentUserId);
    const chatMessages: Message[] = allUserMessages[chatIdToRefresh] || [];

    const visibleMessages = chatMessages.filter((msg) => !msg.isDeletedForMe);
    if (visibleMessages.length === 0) {
      // Nenhuma mensagem visível
      get().updateChatWithNewMessage(
        chatIdToRefresh,
        {
          // "Limpa" o preview
          id: "",
          content: "",
          senderId: "",
          timestamp: new Date(0).toISOString(),
          isOwn: false,
          status: "sent",
          type: "text",
          viewOnce: false,
          viewedAt: null,
          isEdited: null,
          reaction: [],
        },
        currentUserId
      );
      return;
    }

    const nonDeletedMessages = visibleMessages.filter((msg) => msg.type !== "deleted" && !msg.isDeletedForAll);

    let lastMessageForPreview: Message | undefined;

    if (nonDeletedMessages.length > 0) {
      lastMessageForPreview = nonDeletedMessages.reduce((latest, msg) =>
        new Date(msg.timestamp) > new Date(latest.timestamp) ? msg : latest
      );
    } else {
      // Todas as mensagens visíveis foram deletadas para todos
      lastMessageForPreview = visibleMessages.reduce(
        (
          latest,
          msg // Pega a última das deletadas
        ) => (new Date(msg.timestamp) > new Date(latest.timestamp) ? msg : latest)
      );
    }

    if (lastMessageForPreview) {
      get().updateChatWithNewMessage(chatIdToRefresh, lastMessageForPreview, currentUserId);
    }
  },

  clearUnreadMessages: (chatId: string) => {
    set((state) => {
      const chatIndex = state.chats.findIndex((c) => c.id === chatId);
      if (chatIndex === -1) return state;

      const chats = [...state.chats];
      const updatedChat = { ...chats[chatIndex], unreadMessagesCount: 0 };
      chats[chatIndex] = updatedChat;

      // Não precisa reordenar aqui, apenas limpar a contagem
      return { chats };
    });
  },

  toggleFavoriteMessage: async (chatId: string, messageId: string): Promise<Message | undefined> => {
    const currentUserId = useAuthStore.getState().user?.id;
    if (!currentUserId) {
      console.warn("toggleFavoriteMessage: User ID not found. Cannot proceed.");
      return undefined;
    }

    // Usar uma abordagem mais robusta para evitar condições de corrida
    const currentState = get();
    let messagesForChat = currentState.chatMessages[chatId];
    let messageExistsInStoreInitially = !!messagesForChat;

    if (!messagesForChat) {
      console.warn(
        `toggleFavoriteMessage: Messages for chat ${chatId} not in store cache. Attempting to load from IndexedDB.`
      );
      try {
        const allUserMessagesFromDB = await loadMessagesFromIndexedDB(currentUserId);
        if (allUserMessagesFromDB && allUserMessagesFromDB[chatId]) {
          messagesForChat = allUserMessagesFromDB[chatId];
          // Atualizar o estado da store com as mensagens carregadas
          set((state) => ({
            chatMessages: { ...state.chatMessages, [chatId]: messagesForChat },
          }));
        } else {
          console.warn(
            `toggleFavoriteMessage: Chat with id ${chatId} not found in IndexedDB or has no messages after attempting load.`
          );
          if (!messageExistsInStoreInitially) return undefined;
        }
      } catch (error) {
        console.error("toggleFavoriteMessage: Error loading messages from IndexedDB", error);
        if (!messageExistsInStoreInitially) return undefined;
      }
    }

    // Obter o estado mais recente da store
    const currentMessagesInStoreForChat = get().chatMessages[chatId];
    if (!currentMessagesInStoreForChat) {
      console.warn(`toggleFavoriteMessage: Messages for chat ${chatId} still not available after potential load.`);
      return undefined;
    }

    // ==> Add detailed logging here
    console.log(`[Debug] toggleFavoriteMessage: Searching for messageId: '${messageId}' in chat: '${chatId}'`);
    console.log(
      `[Debug] toggleFavoriteMessage: Available message IDs in store for this chat:`,
      currentMessagesInStoreForChat.map((m) => m.id)
    );
    // <== End of added logging

    let messageUpdated = false;
    let toggledMessage: Message | undefined = undefined;

    const updatedMessagesInChat = currentMessagesInStoreForChat.map((msg) => {
      if (msg.id === messageId) {
        messageUpdated = true;
        toggledMessage = { ...msg, isFav: !msg.isFav };
        console.log(
          `toggleFavoriteMessage: Mensagem ${msg.id} - isFav mudando de ${msg.isFav} para ${toggledMessage.isFav}`
        );
        return toggledMessage;
      }
      return msg;
    });

    if (!messageUpdated || !toggledMessage) {
      console.warn(`toggleFavoriteMessage: Message with id ${messageId} in chat ${chatId} not found.`);
      // If we loaded messages from DB just for this operation and didn't find the message,
      // and it wasn't in the store initially, we might want to clear the chat from the store cache
      // if (messagesForChat && !messageExistsInStoreInitially) {
      //   set(state => {
      //     const newChatMessages = { ...state.chatMessages };
      //     delete newChatMessages[chatId];
      //     return { chatMessages: newChatMessages };
      //   });
      // }
      return undefined;
    }

    // Atualiza o estado da store sincronicamente com uma abordagem mais robusta
    set((state) => {
      const currentMessages = state.chatMessages[chatId] || [];
      const updatedMessages = currentMessages.map((msg) => {
        if (msg.id === messageId) {
          return { ...msg, isFav: !msg.isFav };
        }
        return msg;
      });
      
      return {
        chatMessages: {
          ...state.chatMessages,
          [chatId]: updatedMessages,
        },
      };
    });

    // Persiste no IndexedDB (assíncrono, não bloqueia o retorno da UI)
    saveMessageToIndexedDB({ messages: { [chatId]: updatedMessagesInChat }, userId: currentUserId })
      .then(() => {
        console.log("toggleFavoriteMessage: Mensagem salva no IndexedDB com novo estado isFav.");
      })
      .catch((err: Error) => {
        console.error("toggleFavoriteMessage: Falha ao salvar no IndexedDB após toggle", err);
      });

    // Retorna a mensagem com o estado como foi aplicado na store
    return toggledMessage;
  },

  setChatMessages: (messages) => set({ chatMessages: messages }),
}), {
  name: "chat-storage",
  partialize: (state) => ({ chats: state.chats }),
}));
